import { Product } from "@/api/product-api";
import { ProductUtil } from "@/utils/ProductUtil";
import { packSetLines } from "@/libs/cart/pack-set-lines";
import { CurrencyIsoCode } from "@/enums";
import { useBuyTactSuit, type Accessory } from "@/store/useBuyTactSuit";
import { useNaverPayEnabled } from "@/hooks/useNaverPayEnabled";
import { useReferralDiscount } from "@/hooks/useReferralDiscount";
import {
  findNaverPayBasePrice,
  buildNaverPayInfoUrl,
} from "@/libs/orders/naverpay-base-price";
import { buildAllLinesOrNone } from "@/libs/orders/naverpay-lines";
import NaverPayButton, {
  toNaverPayOptions,
  type NaverPayItem,
} from "@/v2-components/naverpay/NaverPayButton";
import { useTrans } from "@/utils/useTrans";
import { cn } from "@/libs/utils";
import { useState } from "react";

export function validateNaverPaySelection(
  product: Product,
  selectedSku: string | null,
): boolean {
  const hasProductOptions = product.skuOptions.some(
    (opt) => opt.options.length > 0,
  );
  if (hasProductOptions && !selectedSku) {
    alert("상품 옵션을 선택해 주세요.");
    return false;
  }
  if (selectedSku && ProductUtil.getStockBySku(product, selectedSku) <= 0) {
    alert("선택하신 옵션은 현재 재고가 없어 주문할 수 없습니다.");
    return false;
  }
  return true;
}

/**
 * 청구 대상 액세서리를 네이버페이 라인으로. **하나라도 못 팔면 `null`** (= 주문 불가).
 *
 * @param offeredAccessorySkus **지금 화면에 떠 있는 액세서리의 SKU 집합.**
 *   스토어의 `accessories` 는 옵션/제품이 바뀌어도 자동 정리되지 않아, 목록에서
 *   사라진 항목이 `selected` 로 남는다 (실측: 글러브 사이즈 S→L, Air→Camo).
 *   `selected` 만 보면 화면에 없는 물건이 네이버페이 주문에 들어간다.
 *
 * ⚠️ **두 가지 "제외"를 구별해야 한다.**
 *
 * | 상황 | 처리 |
 * | --- | --- |
 * | 애초에 청구 대상이 아님 (선택 해제 · 화면에서 사라짐) | `continue` — 정상 |
 * | 청구 대상인데 **KRW 로 못 팜** | **`null` → 주문 전체 불가** |
 *
 * 예전엔 둘을 똑같이 `continue` 로 처리해서, **KRW 가격이 없는 액세서리가 조용히
 * 빠진 채 주문이 나갔다** — 레일 총액엔 있는데 주문엔 없는 상태다(#5221).
 * 카트(`cart-naverpay-button`)는 같은 상황에서 버튼을 숨겼으니 두 표면이 갈려 있었다.
 * 정책은 `libs/orders/naverpay-lines` 가 소유한다.
 */
export function buildAccessoryNaverPayItems(
  accessories: Accessory[],
  products: Product[],
  offeredAccessorySkus: Set<string>,
  /** 화면과 같은 referral percentile — 화면 표시가를 보내야 서버가 대조한다(`findNaverPayBasePrice`). */
  referralPercentile = 0,
): NaverPayItem[] | null {
  // 청구 대상만 먼저 고른다 — 이 단계의 제외는 실패가 아니다.
  // 파우치 2개 → 세트도 여기서 접는다 — 카트·바로 결제와 같은 함수(`packSetLines`).
  // 네이버페이는 KR 전용이라 판매 가능 판정도 KRW·KR 로 한다.
  const chargeable = packSetLines(
    accessories.filter((a) =>
      ProductUtil.isChargeableAccessory(a, offeredAccessorySkus),
    ),
    products,
    { currency: CurrencyIsoCode.KRW, countryCode: "KR" },
  );

  return buildAllLinesOrNone(chargeable, (a) => {
    const product = products.find((p) => p.slug === a.slug);
    const basePrice = product
      ? findNaverPayBasePrice(product.prices, {
          slug: product.slug,
          referralPercentile,
        })
      : null;
    // 카탈로그에 없거나 KRW 가격이 없으면 **팔 수 없다** → 주문 전체 불가.
    if (!product || basePrice === null) return null;

    const skuOption = product.skuOptions.find((so) => so.sku === a.sku);
    const options = skuOption?.options.length
      ? toNaverPayOptions(ProductUtil.getOptionsFromSkuOption(skuOption))
      : undefined;

    return {
      productId: product.id,
      slug: product.slug,
      name: product.name,
      basePrice,
      quantity: a.quantity,
      imageUrl: product.thumbnail,
      infoUrl: buildNaverPayInfoUrl(product.slug),
      sku: a.sku,
      selectedOptions: options,
    };
  });
}

/**
 * 구매 상세페이지(PDP) 네이버페이 버튼 — **모든 구매 레일이 공유하는 단일 구현**.
 *
 * ⚠️ 왜 컴포넌트로 빼는가
 *
 * 이 블록이 예전에는 `BuyTactsuitDetailPageTemplate` 안에 두 벌(StatusBar / TactsuitButtons)
 * 로 인라인돼 있었다. #5081 이 PDP 를 리뉴얼하면서 `StatusBar` 가 렌더 경로를 잃었고, 살아남은
 * 사본은 `ScrollHideButtonByFooter`(→ `laptop:hidden`) 안이라 **데스크톱에서 네이버페이가
 * 통째로 사라졌다.** 새 `BuyBox`(데스크톱 메인 레일)와 `CompactBuyBar`(상단 스티키)에는
 * 아무것도 없었다. KR 주력 결제수단이 조용히 빠진 것이고 tsc/lint/jest 로는 안 잡힌다.
 *
 * ⚠️ `basePrice` 규칙 (docs/naverpay-pricing.md)
 *
 * NaverPay V2.1 에는 `discount`/`originalPrice` 필드가 없어 `basePrice` 하나로만 가격을
 * 전달한다. 그래서 **실판매가 = `originalAmount - discountAmount`** 를 보낸다.
 * `displayAmount`(정가/MSRP)를 쓰면 사용자가 정가로 결제된다 — 자주 하는 실수.
 * 이 산식이 표면마다 복사되면 표면마다 보내는 단가가 갈리므로(가격 차이 자체는 네이버페이 주문 거절 사유가
 * 아니다 — `docs/naverpay-pricing.md` §두 응답의 가격 관계) 한 곳에만 둔다(`libs/orders/naverpay-base-price.ts`).
 */
export const ProductNaverPayButton = ({
  product,
  isSoldOut,
  offeredAccessorySkus,
}: {
  product: Product;
  isSoldOut?: boolean;
  /** 화면에 떠 있는 액세서리 SKU 집합. 호출처가 **실효 제품** 기준으로 만들어 넘긴다
   *  (여기 `product` 는 SKU 소유 제품이라 번들일 수 있어 기준으로 쓰면 안 된다). */
  offeredAccessorySkus: Set<string>;
}) => {
  const getSku = useBuyTactSuit((s) => s.getSku);
  const selectedOptions = useBuyTactSuit((s) => s.options);
  const allProducts = useBuyTactSuit((s) => s.products);
  const accessories = useBuyTactSuit((s) => s.accessories);
  const naverPayEnabled = useNaverPayEnabled();
  // 화면 가격과 같은 percentile(마운트 때 읽은 값) — 서버가 이 값으로 화면가를 대조한다.
  const { referralDiscountPercentile } = useReferralDiscount();
  // ⚠️ 훅은 아래 게이트(`return null`)보다 **위**여야 한다 — 조건부 호출 금지.
  // 라벨은 결제 버튼들과 같은 `gnb` 네임스페이스를 쓴다.
  const { t } = useTrans(["gnb"]);
  // ★SDK 가 버튼을 실제로 그렸는가. `useNaverPayEnabled` 는 **설정만** 보므로(국가·버튼키·
  // 허용목록) 이 신호가 없으면 로드 실패 시 "구분선 + 빈 컨테이너"가 남는다 — 라벨이
  // 결제 수단을 약속하는데 수단이 없는 상태다. 게이트를 이 컴포넌트 안에 둔 이유가
  // "하나라도 어긋나면 선만 남는다" 인데, 로드 실패만 게이트 밖에 있었다.
  const [npayReady, setNpayReady] = useState(false);
  // KRW 가격 행이 없거나 값이 이상한 제품(해외 전용 등)은 노출하지 않는다.
  // flag off 면 전역 비활성. 산식·판정은 naverpay-base-price 단일 출처.
  const basePrice = findNaverPayBasePrice(product.prices, {
    slug: product.slug,
    referralPercentile: referralDiscountPercentile,
  });
  // ★ 액세서리도 **노출 차단 사유**다. 하나라도 KRW 로 못 팔면 버튼을 숨겨 부분 주문을
  //   막는다 — 예전엔 그 액세서리만 빠진 채 결제가 진행됐다(#5221).
  //   카트(`cart-naverpay-button`)와 같은 정책이다.
  const accessoryItems = buildAccessoryNaverPayItems(
    accessories,
    allProducts,
    offeredAccessorySkus,
    referralDiscountPercentile,
  );
  if (
    !naverPayEnabled ||
    isSoldOut ||
    basePrice === null ||
    accessoryItems === null
  ) {
    return null;
  }

  return (
    <>
      {/* ★`Express checkout` 구분선 (시안 29116:30958 / 29006:53801).
          네이버페이는 우리 결제 흐름과 **다른 경로**라, 위 버튼들과 같은 묶음으로 보이면
          "회원 결제 다음 단계" 처럼 읽힌다. 선을 그어 별개 수단임을 표시한다.

          ⚠️ **여기(게이트 안쪽)에 두는 게 요점이다.** 호출처에서 그리면 노출 조건을
          `useNaverPayEnabled` · KRW 가격 · 품절 · 액세서리 가격까지 **네 개 다 복제**해야
          하고, 하나라도 어긋나면 `/en` 처럼 네이버페이가 없는 화면에 선만 남는다.
          같은 술어를 두 곳에서 계산해 어긋난 사고가 반복돼 있다(docs/pitfalls.md #27).
          실제 렌더 지점은 **둘**이다 — 데스크톱/모바일 레일(`[slug].tsx`)과 하단 스티키 바.

          글자 크기가 뷰포트로 갈린다: 데스크톱 12/16(`text-sm`), 모바일 10/14(`text-2xs`).
          `laptop` = 1024px 이고 모바일 하단 바는 그 미만에서만 뜨므로 `laptop:` 는
          데스크톱 레일에만 걸린다.

          ★`mt-[18px]` 은 **네이버페이 SDK 가 자기 위에 예약하는 여백을 상쇄**한다.
          SDK 가 `.npay_button_sdk_wrapper`(**class** 다 — id 아님. 데스크톱 `size_medium` /
          모바일 `size_small`, 둘 다 18px) 에 `padding-top: 18px` 를 스스로
          넣는다(우리 코드엔 그 선택자가 없다 — 리포 전체 grep 0건). 혜택·할인 배지가
          그리는 자리라서 **지우면 배지가 버튼 위로 겹친다** — 없애는 게 아니라 맞춘다.

          그래서 컬럼 gap 만 두면 구분선 위/아래가 비대칭이 된다. 실측(390 · /kr):
          버튼행 622~662 → 구분선 674~688(gap 12) → NPAY 컨테이너 700~758(gap 12 + 안쪽 18).
          위에 같은 18 을 더해 **위 30 / 아래 30** 으로 맞춘다. 두 브레이크포인트 모두
          SDK 여백이 18 이라 값은 하나다(gap 은 12/16 로 다르지만 그건 대칭을 안 깬다). */}
      {/* ⚠️ **조건부 렌더가 아니라 `invisible` 이다.** 선을 없앴다 넣으면 SDK 가 뜨는 순간
          네이버페이 버튼이 통째로 밀린다 — 실측 이동 폭 **1440: 318→368(50px)** ·
          **390: 674→718(44px)** 이고, 이건 실패가 아니라 **정상 케이스(KR 대다수)에서 매번**
          일어난다. "실패 시 고아 라벨" 을 "성공 시 레이아웃 이동" 으로 바꾸는 셈이라
          자리를 유지한다 — `docs/pitfalls.md` #44 규칙 2(상태가 배치를 바꿔야 하면
          `visibility: hidden`, `display: none` 은 위험).

          `visibility: hidden` 은 **접근성 트리에서도 제외**되므로 실패 시 스크린리더가
          `Express checkout` 을 읽지 않는다 — 조건부 렌더와 같은 결과다.

          수용한 것: 로드 실패 시 그 자리(위 실측 폭)가 빈 채로 남는다. 실패는 드물고
          성공은 상시라 상시 쪽을 매끄럽게 하는 편을 택했다. */}
      <div
        className={cn(
          "mt-[18px] flex w-full items-center gap-3",
          !npayReady && "invisible",
        )}
      >
        <div className="h-px flex-1 bg-coolgray-20" />
        <span className="whitespace-nowrap text-2xs tracking-[0.4px] text-coolgray-40 laptop:text-sm">
          {t("Express checkout")}
        </span>
        <div className="h-px flex-1 bg-coolgray-20" />
      </div>
      <NaverPayButton
        onReady={() => setNpayReady(true)}
        onValidate={() => validateNaverPaySelection(product, getSku())}
        items={[
          {
            productId: product.id,
            slug: product.slug,
            name: product.name,
            basePrice,
            quantity: 1,
            imageUrl: product.thumbnail,
            infoUrl: buildNaverPayInfoUrl(product.slug),
            sku: getSku() ?? undefined,
            selectedOptions:
              selectedOptions.length > 0
                ? toNaverPayOptions(selectedOptions)
                : undefined,
          },
          ...accessoryItems,
        ]}
      />
    </>
  );
};
